/**
 * FontScaleSwitcher.jsx — a small floating pill (bottom-left) that lets
 * every visitor pick from four site-wide text sizes. The choice is
 * stored in localStorage and applied via a `data-font-scale` attribute
 * on <html>, which the CSS rules in index.css swap the root `font-size`
 * against. Because every Tailwind rem-class inherits from the root, one
 * knob rescales the entire app — no per-component edits needed.
 */
import React, { useEffect, useState } from "react";
import { Type } from "lucide-react";

const KEY = "sp.font-scale";
const LEVELS = [
  { level: "1", label: "S",  title: "Smaller text"  },
  { level: "2", label: "M",  title: "Standard text" },
  { level: "3", label: "L",  title: "Larger text"   },
  { level: "4", label: "XL", title: "Extra large"   },
];

export function applyFontScaleFromStorage() {
  try {
    // Default bumped from "2" to "3" (Larger) in Feb 2026 so seniors
    // land on comfortable text without having to hunt for the toggle.
    const v = localStorage.getItem(KEY) || "3";
    document.documentElement.setAttribute("data-font-scale", v);
  } catch { /* SSR / private mode — ignore */ }
}

export default function FontScaleSwitcher() {
  const [level, setLevel] = useState("3");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(KEY) || "3";
      setLevel(stored);
      document.documentElement.setAttribute("data-font-scale", stored);
    } catch {/* ignore */}
  }, []);

  const pick = (v) => {
    setLevel(v);
    document.documentElement.setAttribute("data-font-scale", v);
    try { localStorage.setItem(KEY, v); } catch {/* ignore */}
  };

  return (
    <div
      className="fixed bottom-24 left-3 z-40 select-none"
      data-testid="font-scale-switcher"
    >
      {open ? (
        <div
          className="flex items-center gap-1 rounded-full border bg-white/95 backdrop-blur px-2 py-1.5 shadow-lg"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <Type className="w-4 h-4 opacity-70 mx-1" />
          {LEVELS.map((opt) => (
            <button
              key={opt.level}
              onClick={() => pick(opt.level)}
              title={opt.title}
              data-testid={`font-scale-${opt.level}`}
              className={
                "px-2.5 py-1 rounded-full text-sm font-semibold transition " +
                (level === opt.level
                  ? "text-white shadow-inner"
                  : "hover:bg-slate-100 text-slate-700")
              }
              style={level === opt.level ? { background: "var(--wofy-primary)" } : {}}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => setOpen(false)}
            title="Collapse"
            className="ml-1 opacity-50 hover:opacity-100 text-xs px-1"
            data-testid="font-scale-close"
          >
            ×
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="rounded-full border bg-white/95 backdrop-blur px-3 py-2 shadow-lg flex items-center gap-1.5 text-sm font-semibold"
          style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
          title="Text size"
          data-testid="font-scale-open"
        >
          <Type className="w-4 h-4" />
          <span className="text-xs">{LEVELS.find((l) => l.level === level)?.label || "M"}</span>
        </button>
      )}
    </div>
  );
}
